/**
 * Breadcrumb, shared navigation crumb trail for record pages.
 *
 * Sits above the lightning-highlight panel on every record page (per D-114
 * record-page discipline). Renders chevron-separated items in Salesforce
 * Lightning style: prior items muted + linked with hover state, last item
 * bold + obsidian + non-link (current location).
 *
 * Example:
 *   <Breadcrumb items={[
 *     { to: '/home', label: 'Home' },
 *     { to: '/connectors', label: 'My connectors' },
 *     { to: `/connectors/${slug}`, label: 'Gmail' },
 *     { label: 'Search Messages' },  // current — no `to`
 *   ]} />
 *
 * Renders: Home › My connectors › Gmail › Search Messages
 */

import type { ReactNode } from 'react';

import { useUiLink } from './UiLink.tsx';

export interface BreadcrumbItem {
  /** Target route. Omit on the last item (the current location). */
  to?: string;
  /** Display label. */
  label: string;
  /** Optional small icon rendered before label (e.g., connector logo). */
  icon?: ReactNode;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

export function Breadcrumb({ items }: BreadcrumbProps) {
  const LinkComponent = useUiLink();
  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <ol className="breadcrumb-list">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={`${item.label}-${i}`} className="breadcrumb-item">
              {item.to !== undefined && !isLast ? (
                <LinkComponent to={item.to} className="breadcrumb-link">
                  {item.icon !== undefined && (
                    <span className="breadcrumb-icon" aria-hidden="true">
                      {item.icon}
                    </span>
                  )}
                  <span>{item.label}</span>
                </LinkComponent>
              ) : (
                <span className="breadcrumb-current" aria-current="page">
                  {item.icon !== undefined && (
                    <span className="breadcrumb-icon" aria-hidden="true">
                      {item.icon}
                    </span>
                  )}
                  <span>{item.label}</span>
                </span>
              )}
              {!isLast && (
                <span className="breadcrumb-separator" aria-hidden="true">
                  ›
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
